import { useEffect, useMemo, useState } from 'react'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { CONTACT_FIELDS } from 'app/lib/workflows/dataContext'
import type { TriggerNodeData } from 'app/types/workflows'
import { type TriggerSample, useTriggerSamples } from './useTriggerSamples'

/** A live sample the whole builder previews against — one real recent record, resolved to concrete
 *  field values (contact + activity/detail), so every message/data view can show what it would say. */
export type PreviewSample = {
  /** Recent records available to preview against (empty for non-activity triggers). */
  samples: TriggerSample[]
  selectedId?: string
  select: (id: string) => void
  /** Merged field values (contact ∪ activity/detail) for the selected record — keyed by field key. */
  sample: Record<string, string>
  label?: string
  loading: boolean
}

// A non-activity trigger so useTriggerSamples pauses when there's no trigger yet (hooks can't be
// conditional). `contact_created` resolves to the `contact` object → not activity-sourced → no query.
const NO_TRIGGER = { kind: `contact_created` } as TriggerNodeData

/** Zapier-style shared test record. Auto-selects the most recent one, resolves its contact so
 *  `{{firstName}}` etc. fill in too, and returns the merged values every step previews against. */
export function usePreviewSample(triggerData?: TriggerNodeData): PreviewSample {
  const { samples, loading } = useTriggerSamples(triggerData ?? NO_TRIGGER)
  const [selectedId, setSelectedId] = useState<string | undefined>()

  // Keep the selection valid: default to the most recent, and re-default if it falls out of the list
  // (e.g. the trigger's object changed, so a fresh set of samples arrived).
  useEffect(() => {
    if (samples.length && (!selectedId || !samples.some((s) => s.id === selectedId))) {
      setSelectedId(samples[0].id)
    }
  }, [samples, selectedId])

  const selected = samples.find((s) => s.id === selectedId) ?? samples[0]

  // Resolve the record's contact so contact-scoped variables ({{firstName}}, {{cellPhone}}, …) render
  // real values in the preview too — the activity/detail fields alone don't cover them. Read-only.
  const [contactFields, setContactFields] = useState<Record<string, string>>({})
  useEffect(() => {
    const id = selected?.contactId
    if (!id) {
      setContactFields({})
      return
    }
    let alive = true
    gqlFetchOne<Record<string, unknown>>(`contact`, id)
      .then((contact) => {
        if (!alive || !contact) return
        const resolved: Record<string, string> = {}
        for (const { key } of CONTACT_FIELDS) {
          const value = contact[key]
          if (value == null || value === ``) continue
          resolved[key] =
            key === `leadOwner` && typeof value === `object`
              ? String((value as { name?: string }).name ?? ``)
              : String(value)
        }
        setContactFields(resolved)
      })
      .catch(() => setContactFields({}))
    return () => {
      alive = false
    }
  }, [selected?.contactId])

  // Detail/activity values win over contact values on key collisions (they're the more specific source).
  const sample = useMemo(() => ({ ...contactFields, ...(selected?.fields ?? {}) }), [contactFields, selected])

  return { samples, selectedId: selected?.id, select: setSelectedId, sample, label: selected?.label, loading }
}
